import React, { useEffect, useState, useCallback } from 'react';
import { Download, Share2, Plus, CheckCircle, ChevronDown } from 'lucide-react';
import '../styles/InstallAppSection.css';

/**
 * Inline "Install App" CTA for the Coming Soon / public landing pages.
 *
 * Behaviour:
 * - Chrome / Edge / Android / Samsung Internet: shows ONE TAP "Install" button.
 *   No expansion, no instructions — tap fires the browser's native install dialog.
 * - iOS Safari: Apple blocks one-tap install. Shows a discreet pill that expands
 *   to compact "Tap Share → Add to Home Screen" steps. (Unavoidable.)
 * - Already installed (display-mode: standalone): subdued "✓ Installed" state.
 * - Browser doesn't support install at all (e.g. desktop Firefox): renders nothing.
 *
 * Shares the captured beforeinstallprompt event globally on `window.__cadenceDeferredPrompt`
 * so this component, the modal `<InstallPrompt />`, and any future caller all see
 * the same install handle (the browser only fires the event once per page load).
 */
const InstallAppSection = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(
    () => (typeof window !== 'undefined' && window.__cadenceDeferredPrompt) || null
  );
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installError, setInstallError] = useState(null);
  const [iosExpanded, setIosExpanded] = useState(false);

  useEffect(() => {
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);
    setIsStandalone(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );

    const onBeforeInstall = (e) => {
      e.preventDefault();
      window.__cadenceDeferredPrompt = e; // share globally
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      window.__cadenceDeferredPrompt = null;
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    const prompt = deferredPrompt || window.__cadenceDeferredPrompt;
    if (!prompt) {
      setInstallError(
        "Your browser couldn't open an install dialog. Try opening this page in Chrome or Edge."
      );
      return;
    }
    setInstallError(null);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      window.__cadenceDeferredPrompt = null;
      setDeferredPrompt(null);
    } catch (err) {
      setInstallError('Install failed. Refresh the page and try again.');
    }
  }, [deferredPrompt]);

  // Already installed → subdued confirmation
  if (isStandalone || installed) {
    return (
      <section className="install-section" data-testid="install-section-installed">
        <div className="install-pill install-pill--installed">
          <CheckCircle size={16} />
          <span>CADENCE is installed</span>
        </div>
      </section>
    );
  }

  // Chrome / Edge / Android / Samsung — ONE TAP install
  if (deferredPrompt) {
    return (
      <section className="install-section" data-testid="install-section">
        <button
          type="button"
          className="install-pill install-pill--cta"
          onClick={handleInstall}
          data-testid="install-app-cta"
          aria-label="Install CADENCE app"
        >
          <Download size={16} className="install-pill-icon" />
          <span className="install-pill-label">Install the CADENCE App</span>
        </button>
        {installError && (
          <p className="install-error" role="alert">{installError}</p>
        )}
      </section>
    );
  }

  // iOS Safari — Apple's no-API path. Expandable, compact steps.
  if (isIOS) {
    return (
      <section className="install-section" data-testid="install-section">
        <button
          type="button"
          className={`install-pill ${iosExpanded ? 'install-pill--open' : ''}`}
          onClick={() => setIosExpanded((v) => !v)}
          aria-expanded={iosExpanded}
          aria-controls="install-ios-panel"
          data-testid="install-section-toggle"
        >
          <Download size={15} className="install-pill-icon" />
          <span className="install-pill-label">Install on iPhone / iPad</span>
          <ChevronDown size={16} className="install-pill-chevron" />
        </button>

        {iosExpanded && (
          <div id="install-ios-panel" className="install-panel install-panel--ios">
            <div className="install-ios-step">
              <Share2 size={16} />
              <span>Tap <strong>Share</strong> in Safari</span>
            </div>
            <div className="install-ios-arrow" aria-hidden="true">↓</div>
            <div className="install-ios-step">
              <Plus size={16} />
              <span>Choose <strong>Add to Home Screen</strong></span>
            </div>
            <div className="install-ios-arrow" aria-hidden="true">↓</div>
            <div className="install-ios-step">
              <CheckCircle size={16} />
              <span>Tap <strong>Add</strong></span>
            </div>
            <p className="install-ios-note">
              Apple doesn&rsquo;t allow apps to install with one tap on&nbsp;iOS.
            </p>
          </div>
        )}
      </section>
    );
  }

  // Browser supports neither (Firefox desktop, etc.) → render nothing
  return null;
};

export default InstallAppSection;
